import React, { FormEvent, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';

const PAGE_PASSWORD = '12704282';
const ACCESS_KEY = 'finaconnect:tgpay-kyc-access';

const SESSION_URLS: Record<string, string> = {
  'CHAT-260810-1': 'https://verify.didit.me/session/ArXtImPFnUwd',
  'CHAT-260810-2': 'https://verify.didit.me/session/w0iFjrjrv9Qg',
};

const Fonts = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400;1,600&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap');
`;

const Nav = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 3.5rem;
  background: rgba(255, 255, 255, 0.97);
  backdrop-filter: blur(16px);
  border-bottom: 1px solid #e8e2d9;

  @media (max-width: 768px) {
    padding: 1rem 1.5rem;
  }
`;

const NavLogo = styled.img`
  height: 32px;
  object-fit: contain;
  cursor: pointer;
`;

const NavLinks = styled.div`
  display: flex;
  gap: 2.5rem;
  align-items: center;

  @media (max-width: 640px) {
    display: none;
  }
`;

const NavLink = styled.button`
  background: none;
  border: none;
  font-family: 'DM Sans', sans-serif;
  font-size: 0.8125rem;
  font-weight: 500;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #4b5563;
  cursor: pointer;
  transition: color 0.2s;
  padding: 0;

  &:hover {
    color: #0d1b2a;
  }
`;

const NavCta = styled.button`
  background: #1aada0;
  color: #fff;
  border: none;
  font-family: 'DM Sans', sans-serif;
  font-size: 0.8125rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  padding: 0.625rem 1.375rem;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #22c4b5;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  border: 1px solid #d9e4df;
  border-radius: 4px;
  background: #fff;
  color: #0d1b2a;
  padding: 7px 9px;
  cursor: pointer;

  span {
    display: block;
    width: 20px;
    height: 2px;
    margin: 4px 0;
    background: currentColor;
  }

  @media (max-width: 640px) {
    display: block;
  }
`;

const MobileMenu = styled.div`
  position: fixed;
  top: 73px;
  left: 0;
  right: 0;
  z-index: 199;
  display: none;
  flex-direction: column;
  gap: 18px;
  padding: 20px 24px 24px;
  background: rgba(255, 255, 255, 0.98);
  border-bottom: 1px solid #e8e2d9;
  box-shadow: 0 12px 24px rgba(13, 27, 42, 0.08);

  @media (max-width: 640px) {
    display: flex;
  }
`;

const MobileNavLink = styled(NavLink)`
  text-align: left;
  padding: 4px 0;
`;

const MobileNavCta = styled(NavCta)`
  width: 100%;
`;

const SiteNav: React.FC = () => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const go = (path: string) => {
    setMenuOpen(false);
    navigate(path);
  };

  const contact = () => {
    setMenuOpen(false);
    window.location.href = 'mailto:support@finaconnect.com';
  };

  return (
    <>
      <Nav>
        <NavLogo src="https://fctech.cc/wp-content/uploads/2025/09/FinaConnect-Logo-2.png" alt="FinaConnect" onClick={() => go('/')} />
        <NavLinks>
          <NavLink onClick={() => go('/register')}>Onboarding</NavLink>
          <NavLink onClick={() => go('/training')}>AML Training</NavLink>
          <NavLink onClick={contact}>Contact</NavLink>
          <NavCta onClick={() => go('/register')}>Apply Now</NavCta>
        </NavLinks>
        <MobileMenuButton
          type="button"
          aria-label="Toggle navigation menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((open) => !open)}
        >
          <span />
          <span />
          <span />
        </MobileMenuButton>
      </Nav>
      {menuOpen && (
        <MobileMenu>
          <MobileNavLink onClick={() => go('/register')}>Onboarding</MobileNavLink>
          <MobileNavLink onClick={() => go('/training')}>AML Training</MobileNavLink>
          <MobileNavLink onClick={contact}>Contact</MobileNavLink>
          <MobileNavCta onClick={() => go('/register')}>Apply Now</MobileNavCta>
        </MobileMenu>
      )}
    </>
  );
};

const Page = styled.main`
  min-height: 100vh;
  background: #f5faf8;
  color: #17332d;
  padding: 110px 16px 48px;
`;

const Shell = styled.div`
  width: 100%;
  max-width: 960px;
  margin: 0 auto;
`;

const Header = styled.header`
  text-align: center;
  margin: 12px auto 24px;

  h1 {
    margin: 0 0 8px;
    font-size: clamp(1.65rem, 4vw, 2.35rem);
  }

  p {
    margin: 0;
    color: #52706a;
  }
`;

const Card = styled.section`
  background: #fff;
  border: 1px solid #d8ebe5;
  border-radius: 14px;
  box-shadow: 0 8px 30px rgba(23, 51, 45, 0.08);
  padding: 24px;
`;

const PasswordCard = styled(Card)`
  max-width: 420px;
  margin: 72px auto 0;

  h2 {
    margin-top: 0;
  }

  label {
    display: block;
    margin: 18px 0 8px;
    font-weight: 600;
  }

  input {
    box-sizing: border-box;
    width: 100%;
    border: 1px solid #b9d8cf;
    border-radius: 8px;
    padding: 12px;
    font-size: 1rem;
  }
`;

const Button = styled.button`
  width: 100%;
  margin-top: 16px;
  border: 0;
  border-radius: 8px;
  padding: 13px 18px;
  background: #75c9ab;
  color: #12352b;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    background: #62b998;
  }
`;

const ErrorText = styled.p`
  margin: 12px 0 0;
  color: #b42318;
`;

const Iframe = styled.iframe`
  display: block;
  width: 100%;
  min-height: 820px;
  border: 0;
  border-radius: 0;
  background: transparent;
`;

const VerificationIntro = styled.p`
  max-width: 720px;
  margin: 0 auto 12px;
  color: #52706a;
  line-height: 1.55;
  text-align: center;
`;

const TgpayKycPage: React.FC = () => {
  const { pageId } = useParams<{ pageId: string }>();
  const sessionUrl = pageId ? SESSION_URLS[pageId] : undefined;
  const accessKey = pageId ? `${ACCESS_KEY}:${pageId}` : ACCESS_KEY;
  const [unlocked, setUnlocked] = useState(() =>
    sessionStorage.getItem(accessKey) === 'true'
  );
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const pageLabel = useMemo(() => pageId || 'TGpay', [pageId]);

  const handleUnlock = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (password === PAGE_PASSWORD) {
      sessionStorage.setItem(accessKey, 'true');
      setUnlocked(true);
      setError('');
      return;
    }

    setError('Incorrect password. Please try again. 密码错误，请重试。');
  };

  if (!sessionUrl) {
    return (
      <>
        <Fonts />
        <SiteNav />
        <Page>
          <Shell>
            <PasswordCard>
              <h2>Page not found</h2>
              <h2>页面不存在</h2>
              <p>Please check the verification link and try again.</p>
              <p>请检查验证链接后重试。</p>
            </PasswordCard>
          </Shell>
        </Page>
      </>
    );
  }

  if (!unlocked) {
    return (
      <>
        <Fonts />
        <SiteNav />
        <Page>
          <Shell>
            <PasswordCard>
              <h2>TGPay Identity Verification</h2>
              <h2>TGPay 身份验证</h2>
              <p>Please enter the password provided by FinaConnect to continue.</p>
              <p>请输入 FinaConnect 提供的密码继续。</p>
              <form onSubmit={handleUnlock}>
                <label htmlFor="kyc-page-password">Password / 密码</label>
                <input
                  id="kyc-page-password"
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoComplete="off"
                  autoFocus
                />
                <Button type="submit">Continue / 继续</Button>
                {error && <ErrorText role="alert">{error}</ErrorText>}
              </form>
            </PasswordCard>
          </Shell>
        </Page>
      </>
    );
  }

  return (
    <>
      <Fonts />
      <SiteNav />
      <Page>
        <Shell>
          <Header>
            <h1>TGPay Identity Verification</h1>
            <h1>TGPay 身份验证</h1>
            <p>Verification reference: {pageLabel}</p>
            <p>验证编号：{pageLabel}</p>
          </Header>
          <VerificationIntro>
            Please use your mobile phone to complete the identity verification. You will be asked to scan your ID document and complete the biometric checks.
            <br />
            请使用您的手机完成身份验证。您需要扫描身份证件并完成生物识别检查。
          </VerificationIntro>
          <Iframe
            src={sessionUrl}
            title="TGPay identity verification"
            allow="camera; microphone; fullscreen; autoplay; encrypted-media"
          />
        </Shell>
      </Page>
    </>
  );
};

export default TgpayKycPage;
